import { useCallback, useEffect, useState } from 'react';
import type {
  SupportMessageItem,
  SupportMessageStatus,
} from '@shared/types';
import { SUPPORT } from '@shared/constants';
import PageHeader from '../../components/PageHeader';
import PageSection from '../../components/PageSection';
import Alert from '../../components/Alert';
import Pill, { type PillVariant } from '../../components/Pill';
import {
  listSupportMessages,
  getSupportMessage,
  updateSupportMessage,
} from '../../services/support';
import { ApiError } from '../../services/api';

function statusVariant(status: SupportMessageStatus): PillVariant {
  return status === 'resolved' ? 'success' : 'info';
}

function truncate(s: string, max = 80): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1).trimEnd()}…`;
}

export default function SupportMessagesPage() {
  const [items, setItems] = useState<SupportMessageItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SupportMessageStatus | 'all'>('open');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<SupportMessageItem | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const [adminNote, setAdminNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await listSupportMessages({
        status: statusFilter === 'all' ? undefined : statusFilter,
        page: 1,
        pageSize: 50,
      });
      setItems(res.data);
    } catch (err) {
      setLoadError(
        err instanceof ApiError ? err.message : 'Could not load support messages.'
      );
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    setDetailError(null);
    setSubmitError(null);
    try {
      const row = await getSupportMessage(id);
      setSelected(row);
      setAdminNote(row.adminNote ?? '');
    } catch (err) {
      setDetailError(
        err instanceof ApiError ? err.message : 'Could not load message detail.'
      );
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) {
      loadDetail(selectedId);
    } else {
      setSelected(null);
      setAdminNote('');
    }
  }, [selectedId, loadDetail]);

  async function handleStatusToggle(target: SupportMessageStatus): Promise<void> {
    if (!selected) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await updateSupportMessage(selected.id, { status: target });
      setSelected(updated);
      // Refresh list so the row's status pill is in sync.
      await load();
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : 'Could not update message.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveNote(): Promise<void> {
    if (!selected) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const updated = await updateSupportMessage(selected.id, {
        adminNote: adminNote.trim() === '' ? null : adminNote.trim(),
      });
      setSelected(updated);
    } catch (err) {
      setSubmitError(
        err instanceof ApiError ? err.message : 'Could not save note.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div data-testid="support-messages-page">
      <PageHeader
        title="Support Messages"
        subtitle="In-app messages submitted through Settings → Contact Us."
      />

      <PageSection
        title="Inbox"
        testId="support-inbox"
        actions={
          <div className="flex items-center gap-2">
            <label htmlFor="support-status" className="text-xs text-slate-500">
              Status
            </label>
            <select
              id="support-status"
              data-testid="support-status-filter"
              value={statusFilter}
              onChange={(e) =>
                setStatusFilter(
                  e.target.value as SupportMessageStatus | 'all'
                )
              }
              className="border border-slate-300 rounded px-2 py-1 text-sm"
            >
              <option value="open">Open</option>
              <option value="resolved">Resolved</option>
              <option value="all">All</option>
            </select>
          </div>
        }
      >
        {loading && <p className="text-sm text-slate-500">Loading…</p>}
        {loadError && (
          <Alert tone="error" testId="support-load-error">
            {loadError}
          </Alert>
        )}
        {!loading && !loadError && items.length === 0 && (
          <p className="text-sm text-slate-500" data-testid="support-empty">
            No messages.
          </p>
        )}
        {!loading && items.length > 0 && (
          <table className="w-full text-sm" data-testid="support-table">
            <thead className="text-left text-xs uppercase text-slate-500">
              <tr>
                <th className="py-2 pr-4">When</th>
                <th className="py-2 pr-4">User</th>
                <th className="py-2 pr-4">Subject</th>
                <th className="py-2 pr-4">Lang</th>
                <th className="py-2 pr-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr
                  key={m.id}
                  className={
                    'border-t border-slate-200 cursor-pointer hover:bg-slate-50' +
                    (selectedId === m.id ? ' bg-slate-100' : '')
                  }
                  onClick={() => setSelectedId(m.id)}
                  data-testid={`support-row-${m.id}`}
                >
                  <td className="py-2 pr-4 text-slate-600">
                    {new Date(m.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 pr-4 font-mono text-xs text-slate-700">
                    {m.username}
                  </td>
                  <td className="py-2 pr-4 font-medium text-slate-900">
                    {truncate(m.subject)}
                  </td>
                  <td className="py-2 pr-4 text-slate-600">{m.language}</td>
                  <td className="py-2 pr-4">
                    <Pill variant={statusVariant(m.status)}>{m.status}</Pill>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </PageSection>

      {selectedId && (
        <PageSection title="Detail" testId="support-detail">
          {detailLoading && <p className="text-sm text-slate-500">Loading…</p>}
          {detailError && (
            <Alert tone="error" testId="support-detail-error">
              {detailError}
            </Alert>
          )}
          {selected && (
            <div className="space-y-3">
              <div className="text-sm text-slate-700">
                <strong>From:</strong> {selected.username} ({selected.userId})
              </div>
              <div className="text-sm text-slate-700">
                <strong>Submitted:</strong>{' '}
                {new Date(selected.createdAt).toLocaleString()} ·{' '}
                <strong>Lang:</strong> {selected.language}
                {selected.clientInfo && (
                  <>
                    {' '}
                    · <strong>Platform:</strong>{' '}
                    {selected.clientInfo.platform ?? '?'}{' '}
                    {selected.clientInfo.appVersion ?? ''}{' '}
                    ({selected.clientInfo.osVersion ?? '?'})
                  </>
                )}
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">
                  Subject
                </div>
                <div
                  className="text-sm text-slate-900 font-medium"
                  data-testid="support-detail-subject"
                >
                  {selected.subject}
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500 mb-1">
                  Body
                </div>
                <div
                  className="text-sm text-slate-900 whitespace-pre-wrap border border-slate-200 rounded p-3 bg-slate-50"
                  data-testid="support-detail-body"
                >
                  {selected.body}
                </div>
              </div>

              <div>
                <label
                  htmlFor="support-admin-note"
                  className="text-xs uppercase text-slate-500 mb-1 block"
                >
                  Internal note (admin-only)
                </label>
                <textarea
                  id="support-admin-note"
                  data-testid="support-admin-note"
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  rows={3}
                  maxLength={SUPPORT.ADMIN_NOTE_MAX_LENGTH}
                  className="w-full border border-slate-300 rounded px-3 py-2 text-sm"
                  disabled={submitting}
                />
                <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                  <span>{adminNote.length}/{SUPPORT.ADMIN_NOTE_MAX_LENGTH}</span>
                  <button
                    type="button"
                    data-testid="support-save-note"
                    onClick={handleSaveNote}
                    disabled={submitting}
                    className="rounded border border-slate-300 text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Save note
                  </button>
                </div>
              </div>

              {submitError && (
                <Alert tone="error" testId="support-submit-error">
                  {submitError}
                </Alert>
              )}

              <div className="flex items-center gap-2 pt-2">
                <Pill variant={statusVariant(selected.status)}>
                  {selected.status}
                </Pill>
                {selected.status === 'open' ? (
                  <button
                    type="button"
                    data-testid="support-mark-resolved"
                    onClick={() => handleStatusToggle('resolved')}
                    disabled={submitting}
                    className="rounded bg-slate-900 text-white text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Mark resolved
                  </button>
                ) : (
                  <button
                    type="button"
                    data-testid="support-reopen"
                    onClick={() => handleStatusToggle('open')}
                    disabled={submitting}
                    className="rounded border border-slate-300 text-sm px-3 py-1 disabled:opacity-50"
                  >
                    Reopen
                  </button>
                )}
                {selected.resolvedAt && (
                  <span className="text-xs text-slate-500">
                    Resolved {new Date(selected.resolvedAt).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}
        </PageSection>
      )}
    </div>
  );
}
